import { DateTime } from 'luxon';

/**
 * Simple date utility helpers to convert UTC dates to local timezone
 * and safely work with date-only values without unexpected timezone shifts.
 */

/**
 * Safely parses a date value into a Luxon DateTime instance using the user's local timezone.
 */
export const parseDateValue = (
  value?: string | Date | DateTime | null,
): DateTime | null => {
  if (!value) {
    return null;
  }

  if (DateTime.isDateTime(value)) {
    return value.isValid ? value : null;
  }

  if (value instanceof Date) {
    const parsedDate = DateTime.fromJSDate(value);
    return parsedDate.isValid ? parsedDate : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    if (trimmed) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        const parsedDateOnly = DateTime.fromISO(trimmed, {
          zone: 'local',
        }).startOf('day');
        if (parsedDateOnly.isValid) {
          return parsedDateOnly;
        }
      }

      const parsedString = DateTime.fromISO(trimmed, { setZone: true }).toLocal();
      if (parsedString.isValid) {
        return parsedString;
      }
    }
  }

  const parsed = DateTime.fromJSDate(new Date(value as any));
  return parsed.isValid ? parsed : null;
};

/**
 * Formats a date string or Date instance into a display string without timezone shifts.
 */
export const formatDateForDisplay = (
  value?: string | Date | DateTime | null,
  format: string = 'MM/dd/yyyy',
): string => {
  let parsed: DateTime | null = null;

  if (typeof value === 'string') {
    parsed = DateTime.fromISO(value, { setZone: true }).toLocal();
  }

  if (!parsed) {
    parsed = parseDateValue(value);
  }

  return parsed ? parsed.toFormat(format) : '';
};

/**
 * Returns today's date at start of day in the user's local timezone as a Luxon DateTime instance.
 */
export const getTodayStartOfDay = (): DateTime => DateTime.local().startOf('day');

/**
 * Returns today's date at start of day as a native Date object.
 */
export const getTodayStartOfDayDate = (): Date => getTodayStartOfDay().toJSDate();

/**
 * Converts UTC date string to local timezone with timezone abbreviation.
 * @param utcDateString - UTC date string
 * @returns Formatted string like '07 Aug 2025, 03:55 PM IST'
 *
 * Example usage:
 * formatToLocalTime('2025-08-07T10:25:00Z') // Returns: '07 Aug 2025, 03:55 PM IST'
 */
export const formatToLocalTime = (utcDateString: string | Date): string => {
  try {
    const parsed = parseDateValue(utcDateString);

    if (!parsed) {
      return 'Invalid Date';
    }

    // Get user's timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return parsed.setZone(timezone).toFormat('dd LLL yyyy, hh:mm a ZZZ');
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};
